import React from "react";

import { Eyebrow, Title } from "../components/CommonComponents";
import MetaInfo from "../components/MetaInfo";
import Header from "../components/Header";
import Footer from "../components/Footer";

import { ArrowUpRight, ShieldIcon } from "../components/CommonComponents";

const conductItems = [
    {
        number: "01",
        title: "Faculty Code of Conduct",
        label: "Academic Integrity",
        description: "Promotes academic integrity, fair assessment, mentorship, professionalism, and responsible engagement with students and the wider university community.",
        url: "/code-of-conduct-faculty"
    },
    {
        number: "02",
        title: "Student Code of Conduct",
        label: "Responsible Citizenship",
        description: "Focuses on behaviour, discipline, participation in academic and extracurricular activities, and the values expected from responsible members of the university community.",
        url: "/code-of-conduct-student"
    },
    {
        number: "03",
        title: "Management Code of Conduct",
        label: "Responsible Governance",
        description: "Supports responsible governance, transparent decision-making, accountability, and respect for the institutional values and academic purpose of the University.",
        url: "/code-of-conduct-management"
    },
    {
        number: "04",
        title: "Hostel Board Code of Conduct",
        label: "Residential Wellbeing",
        description: "Emphasises community living, safety, discipline, student welfare, and shared responsibility within University residential facilities.",
        url: "/code-of-conduct-boardes-of-hostel"
    },
];

export default function CodeOfConduct() {
    return (
        <>
            <MetaInfo param="" id="about" />
            <Header school="common" />

            <main className="overflow-hidden bg-white text-[#132337]">

                {/* HERO */}
                <section className="relative isolate overflow-hidden bg-[#071A2F] pt-[100px] text-white">
                    <div className="absolute inset-0 -z-30 bg-[radial-gradient(circle_at_82%_20%,rgba(213,173,87,0.18),transparent_28%),radial-gradient(circle_at_12%_84%,rgba(139,30,63,0.24),transparent_32%)]" />
                    <div className="absolute inset-0 -z-20 opacity-[0.05] [background-image:linear-gradient(rgba(255,255,255,.3)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.3)_1px,transparent_1px)] [background-size:72px_72px]" />
                    <div className="absolute -right-[180px] top-[70px] -z-10 h-[620px] w-[620px] rounded-full border border-white/10" />

                    <div className="mx-auto grid min-h-[590px] w-[min(1380px,calc(100%-48px))] items-center gap-14 pb-20 max-sm:w-[calc(100%-28px)] lg:grid-cols-[1.05fr_.95fr]">

                        <div>
                            <div className="flex items-center gap-4 text-sm font-bold uppercase tracking-[0.18em] text-[#D5AD57]">
                                <span className="h-px w-12 bg-current" />
                                Code of Conduct
                            </div>

                            <h1 className="mt-7 max-w-[850px] font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(54px,6.8vw,96px)] font-normal leading-[0.94] tracking-[-0.05em]">
                                Integrity in action.
                                <span className="block text-[#D5AD57]">Responsibility in every role.</span>
                            </h1>

                            <p className="mt-8 max-w-[720px] text-[17px] leading-8 text-white/65">
                                ISBM University is committed to nurturing a culture of integrity, discipline, and mutual respect across all levels of its academic and administrative structure.
                            </p>
                        </div>

                        <div className="relative hidden min-h-[480px] lg:block">
                            <div className="absolute right-0 top-0 w-[390px] rounded-[34px] border border-white/10 bg-white/[0.06] p-8 backdrop-blur-xl">
                                <div className="flex items-center justify-between">
                                    <span className="text-[10px] font-bold uppercase tracking-[0.17em] text-[#D5AD57]">
                                        Guiding Principles
                                    </span>

                                    <span className="grid h-10 w-10 place-items-center rounded-full bg-[#D5AD57] text-[#071A2F]">
                                        <ShieldIcon />
                                    </span>
                                </div>

                                <div className="mt-12 space-y-6">
                                    {[
                                        "Integrity",
                                        "Professionalism",
                                        "Accountability",
                                        "Mutual Respect",
                                    ].map((item, index) => (
                                        <div key={item} className="flex items-center justify-between border-b border-white/10 pb-5">
                                            <span className="text-sm font-semibold text-white/80">
                                                {item}
                                            </span>

                                            <span className="font-['Libre_Caslon_Display',Georgia,serif] text-[24px] text-[#D5AD57]">
                                                {String(index + 1).padStart(2, "0")}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="absolute bottom-0 left-0 w-[240px] bg-[#8B1E3F] p-7 shadow-[0_24px_70px_rgba(0,0,0,.25)]">
                                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#D5AD57]">
                                    University Community
                                </span>

                                <p className="mt-4 font-['Libre_Caslon_Display',Georgia,serif] text-[27px] leading-[1.2]">
                                    Shared values. Shared responsibility.
                                </p>
                            </div>
                        </div>

                    </div>
                </section>


                {/* INTRODUCTION */}
                <section className="bg-white py-20 sm:py-24 lg:py-28">
                    <div className="mx-auto grid w-[min(1380px,calc(100%-48px))] gap-12 max-sm:w-[calc(100%-28px)] lg:grid-cols-[.72fr_1.28fr] lg:gap-20">

                        <div>
                            <Eyebrow>Our Ethical Framework</Eyebrow>

                            <Title
                                before="Standards That"
                                highlight="Shape Our Community"
                                after=""
                                position="middle"
                                theme="burgundy"
                            />
                        </div>

                        <div className="lg:pt-8">
                            <p className="font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(28px,3vw,42px)] font-normal leading-[1.35] text-[#071A2F]">
                                Our Code of Conduct serves as a guiding framework to uphold ethical behavior, professionalism, and accountability.
                            </p>

                            <p className="mt-8 max-w-[820px] text-[16px] leading-8 text-[#65727E]">
                                Every member of the university community, including faculty, students, management, and hostel authorities, is expected to adhere to these standards to maintain a safe, inclusive, and empowering learning environment.
                            </p>
                        </div>

                    </div>
                </section>


                {/* WHY IT MATTERS */}
                <section className="bg-[#F7F2E8] py-20 sm:py-24 lg:py-28">
                    <div className="mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">

                        <div className="grid gap-12 lg:grid-cols-[.7fr_1.3fr] lg:gap-20">

                            <div>
                                <Eyebrow>Shared Responsibility</Eyebrow>

                                <Title
                                    before="One Community."
                                    highlight="Four Clear Responsibilities."
                                    after=""
                                    position="middle"
                                    theme="burgundy"
                                />

                                <p className="mt-7 max-w-[460px] text-[15px] leading-8 text-[#65727E]">
                                    The University defines expectations across academic, student, administrative, and residential life so that every member understands their role in maintaining a respectful and responsible campus.
                                </p>
                            </div>


                            <div className="grid gap-px bg-[#071A2F]/10 sm:grid-cols-2">

                                {[
                                    ["Academic Integrity", "Faculty"],
                                    ["Responsible Behaviour", "Students"],
                                    ["Transparent Governance", "Management"],
                                    ["Safe Community Living", "Hostel"],
                                ].map(([title, label], index) => (
                                    <article key={title} className={`p-8 sm:p-9 ${index === 3 ? "bg-[#071A2F] text-white" : index === 2 ? "bg-[#F4E8EC]" : index === 1 ? "bg-[#EEF4F8]" : "bg-white"}`}>

                                        <span className={`text-[10px] font-bold uppercase tracking-[0.15em] ${index === 3 ? "text-[#D5AD57]" : "text-[#8B1E3F]"}`}>
                                            {label}
                                        </span>

                                        <strong className={`mt-9 block font-['Libre_Caslon_Display',Georgia,serif] text-[34px] font-normal leading-[1.1] ${index === 3 ? "text-white" : "text-[#071A2F]"}`}>
                                            {title}
                                        </strong>

                                        <span className="mt-8 block font-['Libre_Caslon_Display',Georgia,serif] text-[42px] font-normal text-[#D5AD57]">
                                            {String(index + 1).padStart(2, "0")}
                                        </span>

                                    </article>
                                ))}

                            </div>

                        </div>

                    </div>
                </section>


                {/* CODE DIRECTORY */}
                <section className="bg-white py-20 sm:py-24 lg:py-28">
                    <div className="mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">

                        <div className="grid gap-10 border-b border-[#071A2F]/15 pb-10 lg:grid-cols-[1fr_430px] lg:items-end">

                            <div>
                                <Eyebrow>Code of Conduct</Eyebrow>

                                <Title
                                    before="Guidelines for"
                                    highlight="Every University Role"
                                    after=""
                                    position="middle"
                                    theme="burgundy"
                                />
                            </div>

                            <p className="text-[15px] leading-8 text-[#65727E]">
                                Explore the Code of Conduct applicable to different members and areas of the ISBM University community.
                            </p>

                        </div>


                        <div className="mt-14 grid gap-6 lg:grid-cols-2">

                            {conductItems.map((item, index) => (

                                <article key={item.number} className="group relative overflow-hidden border border-[#071A2F]/10 bg-white transition duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(7,26,47,0.08)]">

                                    <div className="grid min-h-[330px] sm:grid-cols-[130px_1fr]">

                                        <div className={`flex flex-col justify-between p-6 ${index === 0 ? "bg-[#071A2F] text-white" : index === 1 ? "bg-[#8B1E3F] text-white" : index === 2 ? "bg-[#EEF4F8] text-[#071A2F]" : "bg-[#F7F2E8] text-[#071A2F]"}`}>

                                            <span className={`text-[10px] font-bold uppercase tracking-[0.15em] ${index < 2 ? "text-[#D5AD57]" : "text-[#8B1E3F]"}`}>
                                                {item.label}
                                            </span>

                                            <strong className={`font-['Libre_Caslon_Display',Georgia,serif] text-[56px] font-normal leading-none ${index < 2 ? "text-[#D5AD57]" : "text-[#8B1E3F]"}`}>
                                                {item.number}
                                            </strong>

                                        </div>


                                        <div className="flex flex-col justify-between p-7 sm:p-8">

                                            <div>
                                                <h3 className="font-['Libre_Caslon_Display',Georgia,serif] text-[32px] font-normal leading-[1.1] text-[#071A2F]">
                                                    {item.title}
                                                </h3>

                                                <p className="mt-5 text-[15px] leading-8 text-[#65727E]">
                                                    {item.description}
                                                </p>
                                            </div>


                                            <div className="mt-9 flex items-center justify-between border-t border-[#071A2F]/10 pt-5">

                                                <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#8B1E3F]">
                                                    View Code
                                                </span>

                                                <a href={item.url} target="_blank" className="grid h-11 w-11 place-items-center rounded-full border border-[#071A2F]/10 text-[#8B1E3F] transition group-hover:bg-[#8B1E3F] group-hover:text-white">
                                                    <ArrowUpRight />
                                                </a>

                                            </div>

                                        </div>

                                    </div>

                                </article>

                            ))}

                        </div>

                    </div>
                </section>


                {/* CLOSING */}
                <section className="relative overflow-hidden bg-[#071A2F] py-20 text-white sm:py-24">
                    <div className="absolute -right-20 top-0 h-[420px] w-[420px] rounded-full border border-white/[0.07]" />
                    <div className="absolute right-10 top-24 h-[250px] w-[250px] rounded-full border border-[#D5AD57]/20" />

                    <div className="relative mx-auto grid w-[min(1380px,calc(100%-48px))] gap-10 max-sm:w-[calc(100%-28px)] lg:grid-cols-[.65fr_1.35fr] lg:items-center">

                        <div>
                            <Eyebrow dark>Our Commitment</Eyebrow>

                            <Title
                                before="A Culture Built On"
                                highlight="Respect & Responsibility"
                                after=""
                                position="middle"
                                theme="gold"
                                dark
                            />
                        </div>

                        <p className="max-w-[760px] text-[17px] leading-9 text-white/70">
                            The Code of Conduct reflects ISBM University's commitment to creating a safe, inclusive, disciplined, and empowering environment where academic excellence and responsible citizenship can thrive.
                        </p>

                    </div>
                </section>

            </main>

            <Footer />
        </>
    );
}